import { useState, useEffect } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Badge,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import MDButton from "components/MDButton";

function DashboardNavbar({ absolute, light, isMini, user }) {
  // console.log("🚀 ~ DashboardNavbar ~ user:", user);
  const navigate = useNavigate();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  if (!user) return null;
  const [openMenu, setOpenMenu] = useState(false);
  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  // console.log('route', route)

  // Fetch unseen notification count
  const fetchUnseenCount = async () => {
    if (!user?.user_id) return 0;
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/notifications/${user.user_id}/unseen-count`,
      { withCredentials: true }
    );
    return response.data?.unseenCount || 0;
  };

  const { data: unseenCount } = useQuery({
    queryKey: ["unseenCount", user?.user_id],
    queryFn: fetchUnseenCount,
    enabled: !!user?.user_id,
    refetchInterval: 30000, // Optional: Poll every 30 seconds
  });

  const currentMonthYear = new Date().toLocaleString("en-US", { month: "long" }) + " " + new Date().getFullYear();

  const { data: hasSubmittedMission } = useQuery({
    queryKey: ["hasSubmittedMission", user?.user_id, currentMonthYear],
    queryFn: async () => {
      if (user?.role_id !== 3) return true;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/user-mission/status`,
        {
          params: { month: currentMonthYear, userId: user?.user_id },
          withCredentials: true,
        }
      );
      return response.data?.data?.includes("monthly");
    },
    enabled: !!user?.user_id && user?.role_id === 3 && new Date().getDate() >= 24,
  });

  const { data: attendanceStatus } = useQuery({
    queryKey: ["attendanceStatus", user?.user_id],
    queryFn: async () => {
      if (user?.role_id !== 3) return null;
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yyyy = today.getFullYear();
      const formattedDate = `${dd}-${mm}-${yyyy}`;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/attendance/check`,
        {
          params: { user_id: user.user_id, attendance_date: formattedDate },
          withCredentials: true,
        }
      );
      return response.data?.marked || null;
    },
    enabled: !!user?.user_id && user?.role_id === 3,
    refetchInterval: 60000,
  });

  const theme = useTheme();
  // non-desktop screens (< 1080px)
  const isBelow1080 = useMediaQuery("(max-width:1200px)");

  // show overlay only if sidenav is expanded and screen is below 1080px
  const showOverlay = !miniSidenav && isBelow1080;

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleOpenProfileMenu = (event) => setOpenProfileMenu(event.currentTarget);
  const handleCloseProfileMenu = () => setOpenProfileMenu(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/logout`, null, {
        withCredentials: true,
      });
      localStorage.removeItem("postLoginRedirect");
      setLogoutOpen(false);
      navigate("/authentication/sign-in");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );

  const renderProfileMenu = () => (
    <Menu
      anchorEl={openProfileMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(openProfileMenu)}
      onClose={handleCloseProfileMenu}
      sx={{ mt: 2 }}
      PaperProps={{
        sx: {
          padding: "8px",
          width: "320px",
          borderRadius: "12px",
          boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
        }
      }}
    >
      <MDBox px={2} py={1.5}>
        <MDBox display="flex" alignItems="center" mb={1.5}>
          <Avatar
            src={user.profile_image && user.profile_image !== "null" ? user.profile_image : null}
            sx={{
              bgcolor: user.profile_image && user.profile_image !== "null" ? "transparent" : "info.main",
              width: 48,
              height: 48,
              fontSize: "1.2rem",
              fontWeight: "bold",
              "& img": {
                objectFit: "cover",
                width: "100%",
                height: "100%",
              },
            }}
          >
            {user.user_name?.charAt(0).toUpperCase()}
          </Avatar>
          <MDBox ml={2}>
            <MDTypography variant="button" fontWeight="bold" display="block">
              {user.user_name}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" sx={{ opacity: 0.8 }}>
              {user.email || "No email provided"}
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider sx={{ my: 1, backgroundColor: "rgba(0,0,0,0.06)" }} />
        <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={0.5}>
          <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ fontSize: "0.7rem", opacity: 0.7 }}>
            ROLE
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold" color="info" sx={{ textTransform: "uppercase" }}>
            {user.role_id == 1 ? "Super Admin" : user.role_id == 2 ? "IT Admin" : user.role_id == 3 ? "Green Fellow" : "-"}
          </MDTypography>
        </MDBox>
      </MDBox>
      <Divider sx={{ my: 1 }} />
      {user.role_id !== 1 && (
        <MenuItem onClick={() => { handleCloseProfileMenu(); navigate(`/user-edit/${user.user_id}`); }}>
          <ListItemIcon>
            <Icon>person</Icon>
          </ListItemIcon>
          <MDTypography variant="button" fontWeight="regular" color="text">
            My Profile
          </MDTypography>
        </MenuItem>
      )}
      <MenuItem onClick={() => { handleCloseProfileMenu(); setLogoutOpen(true); }}>
        <ListItemIcon>
          <Icon color="error">logout</Icon>
        </ListItemIcon>
        <MDTypography variant="button" fontWeight="regular" color="error">
          Logout
        </MDTypography>
      </MenuItem>
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <>
      <AppBar
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
      >
        <Toolbar sx={(theme) => navbarContainer(theme)}>
          <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              // sx={navbarMobileMenu}
              onClick={handleMiniSidenav}
            >
              <Icon sx={iconsStyle} fontSize="medium">
                {miniSidenav ? "menu_open" : "menu"}
              </Icon>
            </IconButton>
            <Breadcrumbs
              icon="home"
              homeRoute={user.role_id == 3 ? "/user-dashboard" : "/dashboard"}
              title={(() => {
                const lastSegment = route.filter(Boolean).pop() || "";
                if (route[0] === "mission-tracker") return "Mission Tracker";
                if (lastSegment.includes("--")) {
                  const parts = lastSegment.split("--");
                  return decodeURIComponent(parts[1]).replaceAll("-", " ");
                }
                return decodeURIComponent(lastSegment).replaceAll("-", " ");
              })()}
              route={route[0] === "mission-tracker" ? [] : route}
              light={light}
            />
          </MDBox>
          {isMini ? null : (
            <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
              {/*<MDBox pr={1}>*/}
              {/*  <MDInput label="Search here" />*/}
              {/*</MDBox>*/}
              <MDBox display="flex" alignItems="center" gap={1.5}>
                {user?.role_id === 3 && (
                  <MDButton
                    variant="gradient"
                    color={
                      attendanceStatus?.geotag_img === "On Duty"
                        ? "success"
                        : attendanceStatus?.login_done && attendanceStatus?.logout_done
                        ? "success"
                        : "info"
                    }
                    size="small"
                    onClick={() => navigate("/attendance")}
                    sx={{
                      mr: 0.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      py: 1,
                      "& .MuiIcon-root": {
                        fontWeight: "bold",
                        fontSize: "18px !important",
                        color: "#fff !important",
                      },
                      "& span": {
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "#fff",
                      },
                    }}
                  >
                    <Icon>
                      {attendanceStatus?.geotag_img === "On Duty"
                        ? "work"
                        : attendanceStatus?.login_done && attendanceStatus?.logout_done
                        ? "check_circle"
                        : "calendar_month"}
                    </Icon>
                    <span>
                      {attendanceStatus?.geotag_img === "On Duty"
                        ? "On Duty Marked"
                        : attendanceStatus?.login_done && attendanceStatus?.logout_done
                        ? "Attendance Marked"
                        : "Mark Today Attendance"}
                    </span>
                  </MDButton>
                )}
                {hasSubmittedMission === false &&
                  user?.role_id === 3 &&
                  new Date().getDate() >= 24 && (
                    <MDButton
                      variant="gradient"
                      color="warning"
                      size="small"
                      onClick={() => navigate("/user_mission")}
                      sx={{
                        mr: 0.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        boxShadow: "0 4px 12px rgba(251, 140, 0, 0.4)",
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%": {
                            transform: "scale(1)",
                          },
                          "50%": {
                            transform: "scale(1.04)",
                          },
                          "100%": {
                            transform: "scale(1)",
                          },
                        },
                        "& .MuiIcon-root": {
                          fontWeight: "bold",
                          fontSize: "18px !important",
                          color: "#fff !important",
                        },
                        "& span": {
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#fff",
                        },
                      }}
                    >
                      <Icon>warning</Icon>
                      <span>Mission Tracker Pending</span>
                    </MDButton>
                  )}
                <IconButton
                  size="medium"
                  disableRipple
                  color="inherit"
                  sx={(theme) => ({
                    ...navbarIconButton(theme),
                    color: ({ palette: { dark, white, text } }) => {
                      return light || darkMode ? white.main : dark.main;
                    },
                  })}
                  onClick={() => navigate("/notifications")}
                >
                  <Badge
                    badgeContent={unseenCount}
                    color="error"
                    overlap="circular"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        height: "20px",
                        minWidth: "20px",
                        padding: "0 4px",
                        top: "4px",
                        right: "4px",
                      },
                    }}
                  >
                    <NotificationsIcon
                      sx={(theme) => ({
                        ...iconsStyle(theme),
                        fontSize: "36px !important",
                        color: light || darkMode ? "white" : "inherit",
                      })}
                    />
                  </Badge>
                </IconButton>
                <MDBox
                  display="flex"
                  alignItems="center"
                  onClick={handleOpenProfileMenu}
                  sx={{
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                    },
                  }}
                >
                  <Avatar
                    src={
                      user.profile_image && user.profile_image !== "null"
                        ? user.profile_image
                        : null
                    }
                    sx={{
                      width: 50,
                      height: 50,
                      fontSize: "0.85rem",
                      bgcolor:
                        user.profile_image && user.profile_image !== "null"
                          ? "transparent"
                          : "info.main",
                      fontWeight: "bold",
                      "& img": {
                        objectFit: "cover",
                        width: "100%",
                        height: "100%",
                      },
                    }}
                  >
                    {user.user_name?.charAt(0).toUpperCase()}
                  </Avatar>
                  {/* <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{
                      color: ({ palette: { dark, white, text } }) => {
                        return light || darkMode ? white.main : dark.main;
                      },
                      textTransform: "capitalize",
                      fontSize: "0.9rem",
                      ml: 1,
                    }}
                  >
                    {user.user_name}
                  </MDTypography> */}
                </MDBox>
                {renderProfileMenu()}
              </MDBox>
              <MDBox color={light ? "white" : "inherit"}>
                {/* <Link to="/authentication/sign-in/basic">
                <IconButton sx={navbarIconButton} size="small" disableRipple>
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              </Link> */}

                {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton> */}
                {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Icon sx={iconsStyle}>notifications</Icon>
              </IconButton> */}
                {renderMenu()}
              </MDBox>
            </MDBox>
          )}
        </Toolbar>
        {/* 👇 Overlay when sidenav is closed */}
        {showOverlay && (
          <MDBox
            onClick={() => setMiniSidenav(dispatch, true)} // collapse sidenav
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 1200,
              backgroundColor: "transparent",
            }}
          />
        )}
      </AppBar>
      <Dialog
        open={logoutOpen}
        onClose={() => !isLoggingOut && setLogoutOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1,
            minWidth: "320px",
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h5" fontWeight="medium">
            Confirm Logout
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDTypography variant="body2" color="text">
              Are you sure you want to log out of the CMGFP portal?
            </MDTypography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="text"
            color="secondary"
            onClick={() => setLogoutOpen(false)}
            disabled={isLoggingOut}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            onClick={handleLogout}
            disabled={isLoggingOut}
            startIcon={isLoggingOut ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoggingOut ? "Logging out..." : "Logout"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  user: {},
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  user: PropTypes.objectOf(PropTypes.any),
};

export default DashboardNavbar;
